import React from "react"
import ProgressBar from 'react-bootstrap/ProgressBar'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'

/**
 * Imports yard-count data for a job from a CSV file.
 *
 * Accepted CSV headers:
 * - Pen name column: "Pen Name", "Pen Number", or "Pen Number:"
 * - Yard count column: "Yard Count", "Yardsheet Count", or "Yardsheet Count:"
 * Header matching ignores capitalization, punctuation, and extra spacing.
 *
 * The component keeps the parsed rows in `toBeImported` instead of React state so
 * the upload callbacks can mutate each row with its latest `upload_result` before
 * refreshing the preview list.
 */
class ImportCSV extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      uploading: false,
      count: 0,
      complete: 0,
      errors: 0,
      done: false
    }

    this.organizerArray = null
    this.toBeImported = []

    this.organizerInput = React.createRef()
    this.organizerButton = React.createRef()
    this.csvPenList = React.createRef();

    this.hasBeenOpened = false
  }

  render () {
    try {
      const uploading = this.state.uploading
      const count = this.state.count
      const complete = this.state.complete
      const errors = this.state.errors
      const done = this.state.done
      const failedPensCount = this.failedPens().length
      return (
        <div className="col-lg-12">
          <div className="row">
            {uploading == true &&
              <div>
                <h6 style={{display: "inline"}}>
                  {done ? "Done: " : "Uploading: "}
                  <div style={{display: 'inline'}}>
                    {complete}
                  </div>
                  /
                  <div style={{display: 'inline'}}>
                    {count}
                  </div>
                  {errors > 0 && 
                    <div style={{display: 'inline'}}>
                      {"   "}
                      Errors: 
                      {errors}
                    </div>
                  }
                </h6>
                {done == false && 
                  <div style={{color: "red"}}>
                    Do not leave page until upload is finished
                  </div>
                }
              </div>
            }
            {uploading == false &&
              <h6 style={{display: "inline"}}>
                <div style={{display: 'inline'}}>
                  {count} Total Pens to Update
                </div>
              </h6>
            }
          </div>
          {uploading == true &&
            <div className="row">
              <ProgressBar animated={!done} now={complete/count * 100} style={{width: '100%'}}/>
            </div>
          }
          <div className="row" style={{marginTop: '5px', marginBottom: '5px'}}>
            <input ref={this.organizerInput} type="file" multiple='multiple' name="organizer" onChange={() => this.onInputChanged()}/>
            { ((uploading == true && done == true) || uploading == false) && 
              <button ref={this.imageButton} className="btn btn-primary"  onClick={() => this.organizerInput.current.click()}>
                  Import Data from CSV
              </button>
            }
            {count > 0 &&
              <div align="right" style={{flexGrow: "1"}}>
                {uploading == false &&
                  <Button type="submit" variant="primary" onClick={() => this.submit()}>Submit</Button>
                }

                {errors > 0 && done == true && 
                  <Button type="submit" variant="primary" onClick={() => this.submit(true)}>Resubmit Failed Pens</Button>
                }
              </div>
            }
            {errors > 0 && done == true && failedPensCount > 0 &&
              <Button type="button" variant="secondary" className="mt-2" onClick={() => this.downloadFailedPensCSV()}>Download Failed Pens CSV</Button>
            }
          </div>
          <div className="row">
            <CsvPenList ref={this.csvPenList}/>
          </div>
        </div>
      );
    } catch (error) {
      console.log(error)
      Sentry.captureException(error)
      return null
    }
  }

  /**
   * Reads the selected CSV, converts it into rows, and updates the preview list.
   * Only one CSV is processed even though the file input allows multiple files.
   */
  onInputChanged() {
    const _this = this
    const organizerInput = this.organizerInput.current
    const organizerButton = this.organizerButton.current
    const csvPenList = this.csvPenList.current
    if (organizerInput.files.length == 1) {
      var file = organizerInput.files[0]
      if (validate_file_size(file)) {
        file.text().then(function(csvString){
          _this.organizerArray = CSVToArray(csvString); 
          _this.parseOrganizerArray()
          _this.setState({count: _this.toBeImported.length, complete: 0, uploading: false, errors: 0, done: false})
          csvPenList.setState({csv_data: _this.toBeImported})
        })
      }
    }
  }

  /**
   * Converts the parsed CSV rows into the normalized payload shape used by the
   * import endpoint. Rows missing either required value are skipped.
   */
  parseOrganizerArray() {
    const organizerArray = this.organizerArray
    this.toBeImported = []
    var penNameColumn = null
    var yardCountColumn = null
    for(var i = 0;i<organizerArray[0].length;i++) {
      var columnName = organizerArray[0][i]
      var normalizedColumnName = this.normalizeCSVHeader(columnName)
      if (this.penNameHeaders().includes(normalizedColumnName)) {
        penNameColumn = i
      } else if(this.yardCountHeaders().includes(normalizedColumnName)) {
        yardCountColumn = i
      }
    }

    // Required headers were not found; leave the import list empty.
    if (penNameColumn == null || yardCountColumn == null) {
      return
    }

    for(var i = 1; i<organizerArray.length; i++) {
      var row = organizerArray[i]
      if(row[penNameColumn] && row[yardCountColumn]) {
        this.toBeImported.push({pen_name: row[penNameColumn], yard_count: row[yardCountColumn]})
      }
    }
  }

  /**
   * Converts a CSV header into a comparison key that tolerates capitalization,
   * punctuation, and extra spacing differences.
   */
  normalizeCSVHeader(header) {
    if (header == null) {
      return ""
    }

    return header.toString().toLowerCase().replace(/[^a-z0-9]/g, "")
  }

  /**
   * Accepted normalized header names for the pen name column.
   */
  penNameHeaders() {
    return [
      "penname",
      "pennumber",
      "pen"
    ]
  }

  /**
   * Accepted normalized header names for the yard count column.
   */
  yardCountHeaders() {
    return [
      "yardcount",
      "yardsheetcount"
    ]
  }

  /**
   * Returns rows that failed during the last import attempt.
   */
  failedPens() {
    return this.toBeImported.filter((penRow) => penRow.upload_result == "Failed")
  }

  /**
   * Escapes a single CSV cell so edited downloads can be re-imported safely.
   */
  csvCell(value) {
    const stringValue = value == null ? "" : value.toString()
    return `"${stringValue.replace(/"/g, '""')}"`
  }

  /**
   * Builds a CSV containing only failed pens. The first two headers use friendly
   * names accepted by the import parser, and the extra status column gives the
   * user edit context.
   */
  failedPensCSV() {
    const headers = ["Pen Name", "Yard Count", "Upload Result", "Failure Reason"]
    const rows = this.failedPens().map((penRow) => [
      penRow.pen_name,
      penRow.yard_count,
      penRow.upload_result,
      penRow.failure_reason
    ])

    return [headers].concat(rows).map((row) => {
      return row.map((value) => this.csvCell(value)).join(",")
    }).join("\n")
  }

  /**
   * Downloads the failed rows as a CSV so the user can correct and re-import
   * them without rebuilding the file from scratch.
   */
  downloadFailedPensCSV() {
    if (this.failedPens().length == 0) {
      return
    }

    const blob = new Blob([this.failedPensCSV()], {type: "text/csv;charset=utf-8;"})
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")

    link.href = url
    link.setAttribute("download", `failed_pens_job_${this.props.job_id}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  /**
   * Returns the clearest failure reason available from an import response.
   */
  importFailureReason(response, fallbackReason = "Import failed") {
    if (response?.message) {
      return response.message
    } else if (response?.responseJSON?.message) {
      return response.responseJSON.message
    } else if (response?.responseText) {
      return response.responseText
    } else {
      return fallbackReason
    }
  }

  /**
   * Begins uploading parsed pen rows.
   *
   * When `onlyFailed` is true, successful rows are left untouched and failed rows
   * are reset so the user can retry only the records that did not import.
   */
  async submit(onlyFailed = false) { 
    if(onlyFailed) {
      var csvPenList = this.csvPenList.current
      var cnt_failed = 0
      for(var i = 0; i< this.toBeImported.length;i++) {
        var pen_row = this.toBeImported[i]
        if(pen_row.upload_result == "Failed") {
          cnt_failed += 1
          pen_row.upload_result = null
          pen_row.failure_reason = null
        }
      }
      await this.setState({done: false, uploading: true, count: cnt_failed, complete: 0, errors: 0})
      await csvPenList.setState({csv_data: this.toBeImported})
    } else {
      await this.setState({uploading: true, complete: 0, done: false})
    }

    this.importPenData(0, onlyFailed)
  }

  /**
   * Uploads one pen row, refreshes the preview list, then recursively advances to
   * the next row. Keeping requests sequential makes progress/error counts easier
   * to show and avoids sending a large burst of import requests at once.
   */
  async importPenData(index, onlyFailed = false) {
    var _this = this
    var user_id = this.props.user_id
    var job_id = this.props.job_id
    var csvPenList = this.csvPenList.current

    if (this.toBeImported.length > index) {
      var pen_data = this.toBeImported[index]
      // Retry mode skips rows that previously imported successfully.
      if(onlyFailed && pen_data.upload_result == "Success") {
        _this.importPenData(index+1, onlyFailed)
        return
      }

      try {
        var jform = new FormData();
        jform.append('utf8',"&#x2713;");
        jform.append('authenticity_token',form_authenticity_token());
        jform.append('user_id',user_id);
        
        
        jform.append('pen_name',pen_data.pen_name);
        jform.append('yard_count', pen_data.yard_count)
      
        $.ajax({
          url: Routes.import_csv_admin_job_path(job_id),
          type: "POST",
          data: jform,
          dataType: 'json',
          mimeType: 'multipart/form-data',
          contentType: false,
          cache: false,
          processData: false,
          success: function(response) {
            pen_data.upload_result = response.status
            pen_data.failure_reason = response.status == "Failed" ? _this.importFailureReason(response) : null
            _this.setState((state) => {
              return {
                complete: state.complete + 1,
                errors: response.status == "Failed" ? state.errors + 1 : state.errors
              }
            })
          },
          error: function(request, textStatus, errorThrown) {
            pen_data.upload_result = "Failed"
            pen_data.failure_reason = _this.importFailureReason(request, errorThrown || textStatus || "Request failed")
            _this.setState((state) => {
              return {
                complete: state.complete + 1,
                errors: state.errors + 1
              }
            })
          },
          complete: function() {
            csvPenList.setState({csv_data: _this.toBeImported})
            _this.importPenData(index+1, onlyFailed)
          }
        })
      } catch(err) {
        pen_data.upload_result = "Failed"
        pen_data.failure_reason = err?.message || "Import failed"
        _this.setState((state) => {
          return {
            complete: state.complete + 1,
            errors: state.errors + 1
          }
        })
        csvPenList.setState({csv_data: _this.toBeImported})
        _this.importPenData(index+1, onlyFailed)
      }

    } else {
      // If every row succeeded, return to the job page after the user sees the
      // completed count. Otherwise stay here so failed rows can be retried.
      if(this.failedPens().length == 0) {
        setTimeout(function(){ window.location.href = `/jobs/${job_id}`}, 1000);
      } else {
        _this.setState({done: true})
      }
    }
  }
}

/**
 * Preview/status list for parsed CSV rows.
 *
 * Badge colors:
 * - Gray: row is queued or has been reset for retry.
 * - Green: row imported successfully.
 * - Red: row failed and can be retried.
 */
class CsvPenList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      csv_data: []
    }
  }

  render() {
    const csv_data = this.state.csv_data
    return (
      <div style={{margin: '5px'}}>
        {csv_data.map((value, index) => {
          const badgeText = `${value.pen_name}: ${value.yard_count}`
          if (value.upload_result == null) {
            return <Badge style={{background: "#bfbfbf", margin: '5px'}} key={index}>{badgeText}</Badge>
          } else if (value.upload_result == "Success") {
            return <Badge style={{background: "#008000", margin: '5px', color: "#ffffff"}} key={index}>{badgeText}</Badge>
          } else if (value.upload_result == "Failed") {
            return <Badge variant="danger" style={{margin: '5px'}} title={value.failure_reason} key={index}>{badgeText}</Badge>
          }
        })}
      </div>
    )
  }
}

export default ImportCSV
